import { useEffect, useState } from "react";
import { useFieldArray, useWatch } from "react-hook-form";
import { cityService } from "../../../services/cityService";

const TripDestinationsForm = ({ t, control, register, errors, setValue, countries, lang }) => {
  const { fields, append, remove, swap } = useFieldArray({
    control,
    name: "tripDestinations",
  });
  const destinationsWatch = useWatch({
    control,
    name: "tripDestinations",
  });
  // Keyed by field.id (stable across reorders), not array index, so a row's
  // loaded city list stays attached to that row when destinations are moved.
  const [citiesByField, setCitiesByField] = useState({});

  const handleAdd = () => {
    append({
      countryId: "",
      cityId: "",
      order: fields.length + 1,
      days: "",
    });
  };

  const loadCitiesForField = async (fieldId, countryId) => {
    if (!countryId) {
      setCitiesByField((prev) => ({ ...prev, [fieldId]: [] }));
      return;
    }
    const data = await cityService.getDropdownByCountryAsync(countryId);
    const list = Array.isArray(data) ? data : data?.value ?? [];
    setCitiesByField((prev) => ({ ...prev, [fieldId]: list }));
  };

  const handleCountryChange = async (index, fieldId, countryId) => {
    setValue(`tripDestinations.${index}.countryId`, countryId);
    setValue(`tripDestinations.${index}.cityId`, "");
    await loadCitiesForField(fieldId, countryId);
  };

  const handleMove = (index, direction) => {
    const targetIndex = index + direction;
    if (targetIndex < 0 || targetIndex >= fields.length) return;
    swap(index, targetIndex);
    // Keep the "order" values in sync with the new positions.
    setValue(`tripDestinations.${index}.order`, index + 1);
    setValue(`tripDestinations.${targetIndex}.order`, targetIndex + 1);
  };

  useEffect(() => {
    fields.forEach((field, index) => {
      const dest = destinationsWatch?.[index];
      if (dest?.countryId && !citiesByField[field.id]) {
        loadCitiesForField(field.id, dest.countryId);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fields, destinationsWatch]);

  return (
    <div className="my-card mt-3">
      <div className="my-card-header text-primary">{t("tripDestinations")}</div>
      <div className="my-card-body">
        {fields?.map((item, index) => (
          <div className="my-card mt-3" key={item.id}>
            <div className="row">
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("order")}</label>
                  <div className="d-flex gap-2 align-items-center">
                    <input
                      type="number"
                      min="1"
                      disabled
                      {...register(`tripDestinations.${index}.order`)}
                      className="form-control"
                      placeholder={t("order")}
                      style={{ minWidth: 0 }}
                    />
                    <button
                      type="button"
                      className="btn-secondary flex-shrink-0"
                      disabled={index === 0}
                      onClick={() => handleMove(index, -1)}
                      title={t("moveUp")}
                    >
                      <i className="fas fa-arrow-up"></i>
                    </button>
                    <button
                      type="button"
                      className="btn-secondary flex-shrink-0"
                      disabled={index === fields.length - 1}
                      onClick={() => handleMove(index, 1)}
                      title={t("moveDown")}
                    >
                      <i className="fas fa-arrow-down"></i>
                    </button>
                  </div>
                  <p className="text-danger">
                    {errors?.tripDestinations?.[index]?.order?.message}
                  </p>
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("country")}</label>
                  <select
                    className="form-control"
                    value={destinationsWatch?.[index]?.countryId || ""}
                    onChange={(e) => handleCountryChange(index, item.id, e.target.value)}
                  >
                    <option value="">--{t("country")}--</option>
                    {countries &&
                      countries.map((country) => (
                        <option key={country.id} value={country.id}>
                          {country.name?.[lang]}
                        </option>
                      ))}
                  </select>
                  <p className="text-danger">
                    {errors?.tripDestinations?.[index]?.countryId?.message}
                  </p>
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("city")}</label>
                  <select
                    className="form-control"
                    value={destinationsWatch?.[index]?.cityId || ""}
                    onChange={(e) =>
                      setValue(`tripDestinations.${index}.cityId`, e.target.value)
                    }
                    disabled={!destinationsWatch?.[index]?.countryId}
                  >
                    <option value="">--{t("city")}--</option>
                    {citiesByField[item.id]?.map((city) => (
                      <option key={city.id} value={city.id}>
                        {city.name?.[lang]}
                      </option>
                    ))}
                  </select>
                  <p className="text-danger">
                    {errors?.tripDestinations?.[index]?.cityId?.message}
                  </p>
                </div>
              </div>
              <div className="col-12">
                <div className="form-group">
                  <label className="form-label">{t("daysInThisCity")}</label>
                  <input
                    type="number"
                    min="1"
                    {...register(`tripDestinations.${index}.days`)}
                    className="form-control"
                    placeholder={t("daysInThisCity")}
                  />
                  <p className="text-danger">
                    {errors?.tripDestinations?.[index]?.days?.message}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
        {fields.length > 1 && (
          <div
            className="remove-btn mt-3"
            onClick={() => remove(fields.length - 1)}
          >
            <i className="fas fa-minus plus-icon"></i> {t("remove")}
          </div>
        )}
        <div className="add-btn mt-3" onClick={handleAdd}>
          <i className="fas fa-plus plus-icon"></i> {t("add")}
        </div>
      </div>
    </div>
  );
};
export default TripDestinationsForm;
